import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/fr';
import UserDoc from '../../../../domain/models/UserDoc';
import { UserStatusType } from '../../../helpers';
import { UserCompanyType, UserRole } from '@leedpulse-monorepo/enums';
import { formatPhoneNumber } from '@leedpulse-monorepo/utils';
import { ICreditMap, IPlan, ICompany, ISubscription } from '@leedpulse-monorepo/interfaces';

dayjs.extend(relativeTime);
dayjs.locale('fr');

/** Per-user credits info for display */
export interface UserCreditsInfo {
  /** Remaining credits from the subscription (Leedflow: leedbucks only) */
  remaining: Partial<ICreditMap>;
  /** Total credits from the plan (Leedflow: leedbucks only) */
  total: Partial<ICreditMap>;
  /** Whether this plan has unlimited leedbucks */
  isUnlimitedLeedbucks: boolean;
  /** PAYG (hors forfait) toggle of the subscription — false = coupé */
  payg_enabled?: boolean;
}

export interface UserDisplay {
  id: string;
  avatar: string;
  name: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  plan: string;
  credits_info: UserCreditsInfo | null; // null = no subscription / number
  lastActivity: string | null;
  memberSince: string;
  status: UserStatusType;
  isVip: boolean;
  userDoc: UserDoc;
  sender: string[];
  idSubscription: string | undefined;
}

/**
 * Determine user status: users with an active subscription/number are "Actif",
 * users without a plan are "Inactif".
 */
export function getUserStatus(hasSubscription: boolean): UserStatusType {
  return hasSubscription ? 'Actif' : 'Inactif';
}

export function formatLastActivity(lastLogin: Date | null): string | null {
  if (!lastLogin) return null;

  const loginDate = dayjs(lastLogin);
  const now = dayjs();
  const diffDays = now.diff(loginDate, 'day');

  if (diffDays === 0) {
    return "Aujourd'hui";
  }
  if (diffDays === 1) {
    return 'Hier';
  }
  if (diffDays < 7) {
    return `Il y a ${diffDays} jours`;
  }
  if (diffDays < 14) {
    return 'Il y a 1 semaine';
  }
  if (diffDays < 30) {
    return `Il y a ${Math.floor(diffDays / 7)} semaines`;
  }
  if (diffDays < 60) {
    return 'Il y a 1 mois';
  }
  if (diffDays < 90) {
    return 'Il y a 2 mois';
  }
  return 'Plus de 3 mois';
}

export function formatMemberSince(created: Date | null | undefined): string {
  if (!created) return '-';
  return dayjs(created).format('D MMM YYYY');
}

export interface UserDisplayParams {
  userDoc: UserDoc;
  activeCompany?: ICompany;
  sender?: string[];
  subscription?: ISubscription;
  credits_info?: UserCreditsInfo | null;
  planCache?: Map<string, IPlan>;
}

export function userDocToUserDisplay({
  userDoc,
  activeCompany,
  sender,
  subscription,
  credits_info,
  planCache,
}: UserDisplayParams): UserDisplay {
  const fullName = `${userDoc.firstname} ${userDoc.lastname}`.trim();

  let planName = subscription?.id_plan ?? '-';
  if (subscription?.id_plan) {
    if (planCache) {
      const plan = planCache.get(subscription?.id_plan ?? '');
      if (plan) {
        planName = plan.name ?? plan.id;
      }
    }
  }

  return {
    id: userDoc.id,
    avatar: userDoc.avatar || '',
    name: fullName || userDoc.email,
    firstname: userDoc.firstname,
    lastname: userDoc.lastname,
    email: userDoc.email,
    phone: formatPhoneNumber(userDoc.phone), // User phone number
    plan: planName,
    credits_info: credits_info ?? null,
    lastActivity: formatLastActivity(userDoc.last_login),
    memberSince: formatMemberSince(userDoc.created),
    status: getUserStatus(!!subscription),
    isVip: userDoc.role === UserRole.owner || (activeCompany && userDoc.idCompanies?.[activeCompany.id]?.roles?.includes(UserCompanyType.SUPERVISOR)) || false,
    userDoc,
    sender: sender || [], // User leedflow number
    idSubscription: subscription?.id || undefined,
  };
}
